/**
 * ApiKeyManager Component
 *
 * Manages API keys for programmatic access to the lakehouse
 */

import React, { useState } from 'react';
import { Plus, Copy, Trash2, Eye, EyeOff, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/useToast';
import {
  useApiKeys,
  useCreateApiKey,
  useRegenerateApiKey,
  useRevokeApiKey,
} from '@/features/settings/hooks/useSettings';

export const ApiKeyManager: React.FC = () => {
  const { toast } = useToast();
  const { data: apiKeys, isLoading } = useApiKeys();
  const createApiKey = useCreateApiKey();
  const regenerateApiKey = useRegenerateApiKey();
  const revokeApiKey = useRevokeApiKey();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeySecret, setNewKeySecret] = useState<string | null>(null);
  const [secretDialogIntent, setSecretDialogIntent] = useState<
    'create' | 'regenerate'
  >('create');
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());

  const handleCreateApiKey = async () => {
    if (!newKeyName.trim()) {
      toast({
        title: 'Validation error',
        description: 'Please enter a name for the API key',
        variant: 'destructive',
      });
      return;
    }

    try {
      const result = await createApiKey.mutateAsync({
        name: newKeyName,
      });

      setNewKeySecret(result.secret_key);
      setNewKeyName('');

      toast({
        title: 'API key created',
        description: 'Your new API key has been created. Save it now - it will not be shown again.',
      });
    } catch (error: any) {
      toast({
        title: 'Failed to create API key',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleRevokeApiKey = async (keyId: string, keyName: string) => {
    if (!confirm(`Are you sure you want to revoke the API key "${keyName}"?`)) {
      return;
    }

    try {
      await revokeApiKey.mutateAsync(keyId);
      toast({
        title: 'API key revoked',
        description: `The API key "${keyName}" has been revoked`,
      });
    } catch (error: any) {
      toast({
        title: 'Failed to revoke API key',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleRegenerateApiKey = async (keyId: string, keyName: string) => {
    if (
      !confirm(
        `Are you sure you want to regenerate the API key "${keyName}"? ` +
          'A new secret will be issued and the current key will stop working ' +
          'after its grace period.'
      )
    ) {
      return;
    }

    try {
      const result = await regenerateApiKey.mutateAsync(keyId);

      setSecretDialogIntent('regenerate');
      setNewKeySecret(result.secret_key);
      setIsCreateDialogOpen(true);

      toast({
        title: 'API key regenerated',
        description:
          'Your new API key has been issued. Save it now - it will not be shown again.',
      });
    } catch (error: any) {
      toast({
        title: 'Failed to regenerate API key',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleCopyKey = (key: string) => {
    navigator.clipboard.writeText(key);
    toast({
      title: 'Copied to clipboard',
      description: 'API key has been copied to clipboard',
    });
  };

  const toggleKeyVisibility = (keyId: string) => {
    setVisibleKeys((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(keyId)) {
        newSet.delete(keyId);
      } else {
        newSet.add(keyId);
      }
      return newSet;
    });
  };

  const maskApiKey = (key: string) => {
    if (key.length <= 8) return key;
    return `${key.substring(0, 4)}${'*'.repeat(key.length - 8)}${key.substring(key.length - 4)}`;
  };

  const closeCreateDialog = () => {
    setIsCreateDialogOpen(false);
    setNewKeySecret(null);
    setNewKeyName('');
    setSecretDialogIntent('create');
  };

  const openCreateDialog = () => {
    setSecretDialogIntent('create');
    setNewKeySecret(null);
    setNewKeyName('');
    setIsCreateDialogOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">API Keys</h3>
          <p className="text-sm text-muted-foreground">
            Manage API keys for programmatic access
          </p>
        </div>
        <Button onClick={openCreateDialog} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Create API Key
        </Button>
      </div>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading API keys...</div>
      ) : apiKeys && apiKeys.length > 0 ? (
        <div className="space-y-3">
          {apiKeys.map((apiKey) => (
            <div
              key={apiKey.id}
              className="flex items-center justify-between p-4 border rounded-lg"
            >
              <div className="flex-1 min-w-0 space-y-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium">{apiKey.name}</p>
                  <Badge
                    variant={apiKey.is_active ? 'default' : 'secondary'}
                  >
                    {apiKey.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>

                <div className="flex items-center gap-2">
                  <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
                    {visibleKeys.has(apiKey.id) ? apiKey.key : maskApiKey(apiKey.key)}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleKeyVisibility(apiKey.id)}
                  >
                    {visibleKeys.has(apiKey.id) ? (
                      <EyeOff className="h-3 w-3" />
                    ) : (
                      <Eye className="h-3 w-3" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopyKey(apiKey.key)}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>

                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span>Created: {new Date(apiKey.created_at).toLocaleDateString()}</span>
                  {apiKey.last_used_at && (
                    <span>Last used: {new Date(apiKey.last_used_at).toLocaleDateString()}</span>
                  )}
                  {apiKey.expires_at && (
                    <span>Expires: {new Date(apiKey.expires_at).toLocaleDateString()}</span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  title="Regenerate"
                  aria-label={`Regenerate API key ${apiKey.name}`}
                  onClick={() => handleRegenerateApiKey(apiKey.id, apiKey.name)}
                  disabled={!apiKey.is_active || regenerateApiKey.isPending}
                >
                  <RotateCw className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  title="Revoke"
                  aria-label={`Revoke API key ${apiKey.name}`}
                  onClick={() => handleRevokeApiKey(apiKey.id, apiKey.name)}
                  disabled={!apiKey.is_active}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <p>No API keys yet</p>
          <p className="text-sm">Create your first API key to get started</p>
        </div>
      )}

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {newKeySecret
                ? secretDialogIntent === 'regenerate'
                  ? 'API Key Regenerated'
                  : 'API Key Created'
                : 'Create API Key'}
            </DialogTitle>
            <DialogDescription>
              {newKeySecret
                ? 'Save this key now - it will not be shown again'
                : 'Create a new API key for programmatic access'}
            </DialogDescription>
          </DialogHeader>

          {newKeySecret ? (
            <div className="space-y-4">
              <Alert>
                <AlertDescription>
                  Make sure to copy your API key now. You won't be able to see it again!
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <Label>Your API Key</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={newKeySecret}
                    className="font-mono text-sm"
                  />
                  <Button
                    variant="outline"
                    onClick={() => handleCopyKey(newKeySecret)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="key-name">Name</Label>
                <Input
                  id="key-name"
                  placeholder="e.g., Production API Key"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            {newKeySecret ? (
              <Button onClick={closeCreateDialog}>Done</Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => setIsCreateDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateApiKey}
                  disabled={createApiKey.isPending}
                >
                  {createApiKey.isPending ? 'Creating...' : 'Create'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
